import 'katex/dist/katex.min.css'

import { FC, isValidElement, ReactElement, useMemo } from 'react'
import { getChildDeep } from 'react-nanny'
import { Box, styled } from '@mui/material'
import { renderToStaticMarkup } from 'react-dom/server'
import { MathComponent } from 'mathjax-react'
import { renderKatexAccessible } from '@app/helpers/mathAccessibility'
import { useMathSpeakableContext } from '@app/contexts/MathSpeakableContext'
import parse from 'html-react-parser'

interface MathMLProps {
  itemId: string
  children: JSX.Element
}

enum Encodings {
  katex = 'application/x-tex',
}

export const MathMLComponent: FC<MathMLProps> = ({ itemId, children }) => {
  const { onSpeakableText } = useMathSpeakableContext()

  // Extract LaTeX synchronously during render — getChildDeep is a pure tree
  // traversal with no side effects, so there is no reason to defer it to an effect.
  // Using itemId as a stable proxy for "item content changed" (matches previous behavior).
  const katexFormula = useMemo(() => {
    const annotation = getChildDeep(children, child => {
      if (!isValidElement(child)) return false
      return (child as ReactElement).type === 'annotation'
    }) as ReactElement | undefined

    if (!annotation || annotation?.props?.encoding !== Encodings.katex) return ''

    if (Array.isArray(annotation.props?.children) && annotation.props.children.length === 1) {
      return annotation.props.children[0] as string
    }

    if (typeof annotation.props?.children !== 'string') return ''

    return annotation.props.children as string
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId])

  // Compute KaTeX HTML and speakable text synchronously so both are available
  // on the same render pass that produced katexFormula.
  const katexResult = useMemo(
    () => (katexFormula ? renderKatexAccessible(katexFormula, { displayMode: false }) : null),
    [katexFormula],
  )

  // No KaTeX annotation found — fall back to MathJax MathML rendering
  if (!katexFormula || !katexResult) {
    return (
      <MathMLContainer>
        <MathComponent mathml={`<math>${renderToStaticMarkup(children)}<math/>`} />
      </MathMLContainer>
    )
  }

  const { html, speakable, isPlainNumber } = katexResult

  // Plain numbers render as text (better screen reader pronunciation)
  if (isPlainNumber) {
    return <span>{speakable}</span>
  }

  // Always render with role="math" and aria-label when speakable is available.
  // Inside a choice (inChoice = true), the parent's aria-describedby traverses
  // ContentWrapper in DOM order, using each math element's aria-label — so
  // mixed content like "Slope [= -6] and y-intercept is [(4/3, 0)]" is read
  // fully and in sequence. Outside a choice (prompts, feedback, etc.), the
  // element is the authoritative accessible node, so we also hide the visual
  // KaTeX output to prevent screen readers from reading both the label and the
  // underlying MathML. When speakable is empty, the raw MathML acts as fallback.
  const inChoice = onSpeakableText !== null
  return (
    <KatexContainer
      // VoiceOver can announce the role name ("math") instead of the label when
      // role="math" is present. Prefer the computed aria-label when available,
      // and only fall back to role="math" when we have nothing speakable.
      role={speakable ? 'img' : 'math'}
      aria-label={speakable || undefined}
      {...(!inChoice &&
        speakable && {
          sx: { display: 'inline-block' },
        })}
    >
      {!inChoice && speakable ? (
        <Box component='span' aria-hidden='true'>
          {parse(html)}
        </Box>
      ) : (
        parse(html)
      )}
    </KatexContainer>
  )
}

const KatexContainer = styled(Box)(({ theme: { spacing } }) => ({
  display: 'inline',
  // Add vertical spacing only for complex math (fractions, arrays, etc.)
  '& .katex:has(.frac), & .katex:has(.array), & .katex:has(.mfrac)': {
    '& .base': {
      marginBottom: spacing(2),
      marginTop: '2px',
    },
  },
}))

const MathMLContainer = styled(Box)(() => ({
  display: 'inline',
  '& div': {
    display: 'inherit',
  },
}))
